import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import type { PaginatedReactionResponse } from "@/lib/humhub-api/types";

interface UseInfiniteReactionsQueryOptions<
  TQueryKey extends readonly unknown[],
> {
  queryKey: TQueryKey;
  enabled: boolean;
  queryFn: (page: number) => Promise<PaginatedReactionResponse>;
}

export const useInfiniteReactionsQuery = <
  TQueryKey extends readonly unknown[],
>({
  queryKey,
  enabled,
  queryFn,
}: UseInfiniteReactionsQueryOptions<TQueryKey>) => {
  const queryResult = useInfiniteQuery<
    PaginatedReactionResponse,
    Error,
    InfiniteData<PaginatedReactionResponse>,
    TQueryKey,
    number
  >({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => queryFn(pageParam),
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.page || 1) + 1;
      return nextPage <= (lastPage.pages || 1) ? nextPage : undefined;
    },
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const reactions =
    queryResult.data?.pages.flatMap((page) => page.results) ?? [];
  const total = queryResult.data?.pages?.[0]?.total ?? 0;

  return {
    ...queryResult,
    reactions,
    total,
  };
};
